/**
 * Factory API Types - Zod Schemas & DTOs for Strapi Integration
 * Based on Strapi api::factory.factory schema
 */

import { z } from '@ufc/core'

// ============================================================================
// Strapi Response Meta
// ============================================================================

export const strapiPaginationSchema = z.object({
  page: z.number(),
  pageSize: z.number(),
  pageCount: z.number(),
  total: z.number(),
})

export type StrapiPagination = z.infer<typeof strapiPaginationSchema>

export const strapiMetaSchema = z.object({
  pagination: strapiPaginationSchema.optional(),
})

export type StrapiMeta = z.infer<typeof strapiMetaSchema>

// ============================================================================
// Country DTO (api::country.country)
// ============================================================================

export const countryDtoSchema = z.object({
  id: z.number(),
  documentId: z.string(),
  code: z.string(),
  name: z.string(),
  isActive: z.boolean().default(true),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export type CountryDto = z.infer<typeof countryDtoSchema>

// ============================================================================
// City DTO (api::city.city)
// ============================================================================

export const strapiCityLocalizationSchema = z.object({
  id: z.number().optional(),
  documentId: z.string().optional(),
  locale: z.string(),
  name: z.string().optional(),
})

export const cityDtoSchema = z.object({
  id: z.number(),
  documentId: z.string(),
  code: z.string(),
  name: z.string(),
  stateProvince: z.string().nullable().optional(),
  isActive: z.boolean().default(true),
  country: countryDtoSchema.nullable().optional(),
  locale: z.string().optional(),
  localizations: z.array(strapiCityLocalizationSchema).optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export type CityDto = z.infer<typeof cityDtoSchema>

// ============================================================================
// Location DTO (api::location.location) - from addressbook
// ============================================================================

export const locationStatusSchema = z.enum(['active', 'inactive'])

export const locationDtoSchema = z.object({
  id: z.number(),
  documentId: z.string(),
  code: z.string(),
  name: z.string(),
  addressLine1: z.string().nullable().optional(),
  addressLine2: z.string().nullable().optional(),
  postalCode: z.string().nullable().optional(),
  timezone: z.string().nullable().optional(),
  latitude: z.number().nullable().optional(),
  longitude: z.number().nullable().optional(),
  status: locationStatusSchema.default('active'),
  city: cityDtoSchema.nullable().optional(),
  country: countryDtoSchema.nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export type LocationDto = z.infer<typeof locationDtoSchema>

// ============================================================================
// Factory DTO (api::factory.factory)
// ============================================================================

export const factoryStatusSchema = z.enum(['active', 'inactive'])

export type FactoryStatusDto = z.infer<typeof factoryStatusSchema>

// Strapi localization schema for i18n
export const strapiLocalizationSchema = z.object({
  id: z.number().optional(),
  documentId: z.string().optional(),
  locale: z.string(),
  name: z.string().optional(),
  description: z.string().nullable().optional(),
})

export const factoryDtoSchema = z.object({
  id: z.number(),
  documentId: z.string(),
  code: z.string(),
  sapPlantCode: z.string().nullable().optional(),
  name: z.string(), // Current locale value
  description: z.string().nullable().optional(),
  factoryStatus: factoryStatusSchema.default('active'),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  // Soft delete
  deletedAt: z.string().nullable().optional(),
  deletedBy: z.string().nullable().optional(),
  // Relations (populated)
  location: locationDtoSchema.nullable().optional(),
  cities: z.array(cityDtoSchema).nullable().optional(), // Many-to-many cities relation
  // Timestamps
  createdAt: z.string(),
  updatedAt: z.string(),
  // Strapi i18n fields
  locale: z.string().optional(),
  localizations: z.array(strapiLocalizationSchema).optional(),
})

export type FactoryDto = z.infer<typeof factoryDtoSchema>

// ============================================================================
// Strapi Response Wrappers
// ============================================================================

export const strapiSingleResponseSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
  z.object({
    data: dataSchema,
    meta: strapiMetaSchema.optional(),
  })

export const strapiListResponseSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
  z.object({
    data: z.array(dataSchema),
    meta: strapiMetaSchema.optional(),
  })

// Factory responses
export const factoryResponseSchema = strapiSingleResponseSchema(factoryDtoSchema)
export const factoryListResponseSchema = strapiListResponseSchema(factoryDtoSchema)

export type FactoryResponse = z.infer<typeof factoryResponseSchema>
export type FactoryListResponse = z.infer<typeof factoryListResponseSchema>

// ============================================================================
// Request DTOs
// ============================================================================

export const createFactoryRequestSchema = z.object({
  code: z.string().min(1).max(50).optional(),
  sapPlantCode: z.string().max(10).optional(),
  name: z.string().min(1).max(255),
  description: z.string().optional(),
  factoryStatus: factoryStatusSchema.optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  // Relation IDs (Strapi v5 uses documentId strings for relations)
  location: z.union([z.number(), z.string()]).optional(), // Reference to existing location
  cities: z.array(z.string()).optional(), // Many-to-many relation to cities (documentIds)
})

export type CreateFactoryRequestDto = z.infer<typeof createFactoryRequestSchema>

export const updateFactoryRequestSchema = createFactoryRequestSchema.partial()

export type UpdateFactoryRequestDto = z.infer<typeof updateFactoryRequestSchema>

// ============================================================================
// API Endpoints
// ============================================================================

export const STRAPI_ENDPOINTS = {
  FACTORIES: '/api/factories',
  FACTORY_BY_ID: (documentId: string) => `/api/factories/${documentId}`,
  LOCATIONS: '/api/locations',
  CITIES: '/api/cities',
  COUNTRIES: '/api/countries',
} as const

// ============================================================================
// Query Builder Helpers
// ============================================================================

export interface FactoryQueryParams {
  // Filters
  status?: 'active' | 'inactive'
  countryCode?: string
  search?: string
  // Population
  populate?: ('location' | 'cities')[] | null
  // Pagination
  page?: number
  pageSize?: number
  // Sorting
  sort?: string
  // i18n
  locale?: string
}

export function buildFactoryQuery(params: FactoryQueryParams): Record<string, string | boolean> {
  const query: Record<string, string | boolean> = {}

  // Filters - exclude soft deleted
  query['filters[deletedAt][$null]'] = 'true'

  if (params.status) {
    query['filters[factoryStatus][$eq]'] = params.status
  }
  if (params.countryCode) {
    query['filters[location][country][code][$eq]'] = params.countryCode
  }
  if (params.search) {
    query['filters[$or][0][name][$containsi]'] = params.search
    query['filters[$or][1][code][$containsi]'] = params.search
    query['filters[$or][2][location][name][$containsi]'] = params.search
    query['filters[$or][3][location][addressLine1][$containsi]'] = params.search
  }

  // Population
  if (params.populate !== null) {
    // Always populate localizations (Strapi i18n - get both en and ar in single request)
    query['populate[localizations][fields][0]'] = 'locale'
    query['populate[localizations][fields][1]'] = 'name'
    query['populate[localizations][fields][2]'] = 'description'

    // Deep populate helpers. Arabic names for locations that lack a
    // localization entry are merged in via a separate AR-shim fetch on the
    // frontend (see fetchLocationArNamesMap).
    const populateLocation = () => {
      query['populate[location][fields][0]'] = 'id'
      query['populate[location][fields][1]'] = 'documentId'
      query['populate[location][fields][2]'] = 'name'
      query['populate[location][fields][3]'] = 'addressLine1'
      query['populate[location][populate][city][fields][0]'] = 'id'
      query['populate[location][populate][city][fields][1]'] = 'documentId'
      query['populate[location][populate][city][fields][2]'] = 'name'
      // Country is not i18n-enabled in this workspace.
      query['populate[location][populate][country][fields][0]'] = 'id'
      query['populate[location][populate][country][fields][1]'] = 'documentId'
      query['populate[location][populate][country][fields][2]'] = 'name'
      query['populate[location][populate][country][fields][3]'] = 'code'
    }
    const populateCities = () => {
      query['populate[cities][fields][0]'] = 'id'
      query['populate[cities][fields][1]'] = 'documentId'
      query['populate[cities][fields][2]'] = 'code'
      query['populate[cities][fields][3]'] = 'name'
      query['populate[cities][populate][localizations][fields][0]'] = 'locale'
      query['populate[cities][populate][localizations][fields][1]'] = 'name'
    }

    if (params.populate && params.populate.length > 0) {
      params.populate.forEach((rel) => {
        if (rel === 'location') populateLocation()
        if (rel === 'cities') populateCities()
      })
    } else {
      populateLocation()
      populateCities()
    }
  }

  // Pagination
  if (params.page) {
    query['pagination[page]'] = String(params.page)
  }
  if (params.pageSize) {
    query['pagination[pageSize]'] = String(params.pageSize)
  }

  // Sorting
  if (params.sort) {
    query['sort'] = params.sort
  } else {
    query['sort'] = 'name:asc'
  }

  // i18n locale - default to 'en' so main DTO has English, localizations has Arabic
  query['locale'] = params.locale || 'en'

  return query
}

// ============================================================================
// Validation Helpers
// ============================================================================

export function validateFactoryDto(data: unknown): FactoryDto {
  return factoryDtoSchema.parse(data)
}

export function validateFactoryListResponse(data: unknown): FactoryListResponse {
  return factoryListResponseSchema.parse(data)
}
